import { z } from 'zod';

/**
 * Dataset templates — the contract between a client's export and this platform.
 *
 * One definition per dataset, and it is deliberately the *only* definition:
 * the downloadable workbook template, the upload validator, the column
 * auto-suggester and the exception report are all generated from the objects
 * below. That is the point. A template maintained separately from its
 * validator drifts within one release, and the first symptom is a client
 * filling in a column the importer silently ignores.
 *
 * `key` is the canonical field name written to the database. `aliases` are
 * header spellings seen in the wild, used to auto-suggest a mapping when a
 * client's export does not use our headers — which is the normal case, not the
 * exception. Nothing here assumes the client reshapes their file for us.
 */

/**
 * `datetime` was added with the work-item transition sheet. A transition needs
 * the time of day: a changelog that records only the date cannot tell an item
 * that waited two hours from one that waited two hours short of a week, and
 * flow efficiency is the difference between those two answers.
 */
export type DatasetFieldType =
  | 'text'
  | 'number'
  | 'money'
  | 'date'
  | 'datetime'
  | 'enum'
  | 'boolean';

/**
 * Note on `example` values for identifier fields: they are deliberately
 * prefixed `EXAMPLE-`. Realistic-looking codes (`APP-0001`, `TEAM-014`) match
 * records that already exist, so a client who imports the template without
 * first deleting the sample row silently overwrites live data — observed
 * doing precisely that during testing, renaming a real application.
 */
export interface DatasetField {
  key: string;
  label: string;
  type: DatasetFieldType;
  required: boolean;
  /** Human guidance printed into the template's instructions sheet. */
  description: string;
  /** Permitted values for `enum`; also rendered as a dropdown in the template. */
  allowed?: readonly string[];
  /** Header spellings that should auto-map to this field. Lower-cased on compare. */
  aliases?: readonly string[];
  example: string | number;
}

/**
 * How often a dataset is expected to arrive.
 *
 * This is not decoration: it decides who chases whom. A `Reference` sheet is
 * somebody's one-off statement of what exists and only changes when the estate
 * does; a `Weekly` or `Monthly` one is a standing obligation, and a feed that
 * has missed its window is a finding rather than a preference. The two also
 * fail differently — a stale reference sheet is usually still correct, a stale
 * cost feed is usually not.
 */
export type DatasetCadence = 'Reference' | 'Monthly' | 'Weekly';

export interface DatasetTemplate {
  /** Our own stable key for this sheet. Unique; used in URLs and mapping profiles. */
  id: string;
  /**
   * FK to `ingest.dataset_contract.code`. Several sheets legitimately share one
   * contract — Finance & Benefits (DS-010) covers cost actuals, their
   * allocations and the benefits claimed against them, because they are loaded
   * and reconciled as one submission.
   */
  contractCode: string;
  sheet: string;
  title: string;
  purpose: string;
  /**
   * The list-view line: at most ten words, no trailing full stop.
   *
   * Separate from `purpose` rather than a truncation of it. The intake page has
   * room to explain how rows are matched and what happens on a re-upload; a row
   * in a list of ten datasets has one line, and an owner scanning for theirs
   * needs to recognise it, not read about it.
   */
  summary: string;
  /**
   * The field whose value identifies a row across re-uploads. Never the row
   * number: a client who inserts a row at the top must not re-key their estate.
   */
  identityField: string;
  /** How often this sheet is expected to be resubmitted. See `DatasetCadence`. */
  cadence: DatasetCadence;
  /** The role that usually holds this data — who to send the template to. */
  ownedBy: string;
  fields: readonly DatasetField[];
}

const LIFECYCLE = ['Proposed', 'Candidate', 'Active', 'Sunset', 'Retired', 'Superseded'] as const;
const HEALTH = ['Healthy', 'AtRisk', 'HighRisk', 'Critical', 'Unknown'] as const;
const CRITICALITY = ['MissionCritical', 'BusinessCritical', 'Important', 'Low'] as const;
const TECH_HEALTH = ['Good', 'Degraded', 'AtRisk', 'Poor', 'Unknown'] as const;
const COST_CATEGORY = ['Run', 'Enhance', 'Transform', 'Other'] as const;

const DATASET_DEFINITIONS = [
  {
    id: 'organisation',
    contractCode: 'DS-001',
    sheet: 'Organisation',
    title: 'Organisation hierarchy',
    summary: 'Divisions, value streams and product groups',
    purpose:
      'The reporting structure products hang from: divisions, the value streams inside them, and any product groups.',
    identityField: 'unit_code',
    cadence: 'Reference',
    ownedBy: 'Enterprise architecture',
    fields: [
      {
        key: 'unit_code',
        label: 'Unit code',
        type: 'text',
        required: true,
        description: 'Your own stable identifier for this unit. Must not change between uploads.',
        aliases: ['code', 'id', 'org code', 'unit id', 'department code'],
        // Matches the Products sheet's `parent_unit_code` example, so the two
        // worked rows validate against each other. A template whose own
        // examples fail validation teaches the client to distrust the check.
        example: 'EXAMPLE-VS-1',
      },
      {
        key: 'unit_name',
        label: 'Unit name',
        type: 'text',
        required: true,
        description: 'Name as it should appear on screen.',
        aliases: [
          'name',
          'department',
          'division',
          'title',
          'description',
          'unit',
          'org name',
          'business unit',
        ],
        example: 'Network & Operations',
      },
      {
        key: 'unit_type',
        label: 'Unit type',
        type: 'enum',
        required: true,
        description: 'Which level of the hierarchy this row is.',
        allowed: ['Division', 'ValueStream', 'ProductGroup'],
        aliases: ['type', 'level', 'kind'],
        example: 'ValueStream',
      },
      {
        key: 'parent_code',
        label: 'Parent unit code',
        type: 'text',
        required: false,
        description: 'The unit_code of this row’s parent. Leave blank for a top-level division.',
        aliases: ['parent', 'parent id', 'reports to', 'belongs to'],
        example: '',
      },
      {
        key: 'owner_email',
        label: 'Owner email',
        type: 'text',
        required: false,
        description: 'Person accountable for this unit. Used to link ownership across datasets.',
        aliases: ['owner', 'lead', 'accountable', 'manager email'],
        example: 'alex.moreau@example.com',
      },
    ],
  },
  {
    id: 'products',
    contractCode: 'DS-004',
    sheet: 'Products',
    title: 'Product portfolio',
    summary: 'The products the organisation funds and owns',
    purpose: 'The products the organisation funds, owns and reports on.',
    identityField: 'product_code',
    cadence: 'Reference',
    ownedBy: 'Portfolio management office',
    fields: [
      {
        key: 'product_code',
        label: 'Product code',
        type: 'text',
        required: true,
        description: 'Your stable identifier for the product. Must not change between uploads.',
        aliases: ['code', 'id', 'product id', 'ref'],
        example: 'EXAMPLE-PRD-1',
      },
      {
        key: 'product_name',
        label: 'Product name',
        type: 'text',
        required: true,
        description: 'Name as it should appear on screen.',
        aliases: ['name', 'product', 'title'],
        example: 'Flight Booking',
      },
      {
        key: 'parent_unit_code',
        label: 'Parent unit code',
        type: 'text',
        /**
         * Optional, so that this sheet stands on its own.
         *
         * It was required, which meant the person who owns the product list
         * could not submit anything until somebody else had supplied the
         * organisation sheet — and if they guessed a unit code wrong, all 178
         * rows were rejected rather than the one. Each sheet is now sent by a
         * different person, so a required reference to another person's file
         * is a queue, not a validation.
         *
         * Left blank the product still imports; it simply sits outside the
         * hierarchy until an organisation sheet places it, and the import says
         * so rather than implying the product is filed somewhere.
         */
        required: false,
        description:
          'The value stream or product group this product sits in, from the Organisation sheet. Leave blank if you do not know it — the product will import unplaced and can be filed later.',
        aliases: ['value stream', 'product group', 'parent', 'owning unit'],
        example: 'EXAMPLE-VS-1',
      },
      {
        key: 'lifecycle',
        label: 'Lifecycle',
        type: 'enum',
        required: true,
        description: 'Current lifecycle stage.',
        allowed: LIFECYCLE,
        aliases: ['status', 'stage', 'state', 'lifecycle status'],
        example: 'Active',
      },
      {
        key: 'owner_email',
        label: 'Product owner email',
        type: 'text',
        required: false,
        description: 'Person accountable for the product.',
        aliases: ['owner', 'product owner', 'accountable'],
        example: 'sam.patel@example.com',
      },
      {
        key: 'health_score',
        label: 'Health score',
        type: 'number',
        required: false,
        description:
          '0–100. Leave blank if you do not score product health — it will read as "not assessed", not as zero.',
        aliases: ['health', 'score', 'rag score'],
        example: 72,
      },
      {
        key: 'health_band',
        label: 'Health band',
        type: 'enum',
        required: false,
        description: 'Banding, if you hold one. Leave blank if unassessed.',
        allowed: HEALTH,
        aliases: ['rag', 'band', 'health status'],
        example: 'Healthy',
      },
      {
        key: 'business_value_annual',
        label: 'Business value (annual)',
        type: 'money',
        required: false,
        description:
          'Attributed annual value. Leave blank if the product has not been valued — a blank reads as "not valued", a 0 reads as "worth nothing".',
        aliases: ['value', 'annual value', 'business value', 'benefit value'],
        example: 4200000,
      },
    ],
  },
  {
    id: 'applications',
    contractCode: 'DS-007',
    sheet: 'Applications',
    title: 'Application inventory',
    summary: 'The application estate and what each supports',
    purpose: 'The application estate, and which products each application supports.',
    identityField: 'application_code',
    cadence: 'Reference',
    ownedBy: 'Application owners / CMDB team',
    fields: [
      {
        key: 'application_code',
        label: 'Application code',
        type: 'text',
        required: true,
        description: 'Your stable identifier, typically the CMDB id.',
        aliases: ['code', 'id', 'ci id', 'app id', 'asset id'],
        example: 'EXAMPLE-APP-1',
      },
      {
        key: 'application_name',
        label: 'Application name',
        type: 'text',
        required: true,
        description: 'Name as it should appear on screen.',
        aliases: ['name', 'application', 'system', 'title'],
        example: 'Reservation Core',
      },
      {
        key: 'lifecycle',
        label: 'Lifecycle',
        type: 'enum',
        required: true,
        description: 'Current lifecycle stage.',
        allowed: LIFECYCLE,
        aliases: ['status', 'stage', 'state', 'lifecycle status', 'lifecycle state'],
        example: 'Active',
      },
      {
        key: 'criticality',
        label: 'Business criticality',
        type: 'enum',
        required: false,
        description: 'How critical the application is to operations.',
        allowed: CRITICALITY,
        aliases: ['criticality', 'tier', 'importance', 'business criticality', 'crit'],
        example: 'MissionCritical',
      },
      {
        key: 'technical_health',
        label: 'Technical health',
        type: 'enum',
        required: false,
        description: 'Condition of the application from an engineering standpoint.',
        allowed: TECH_HEALTH,
        aliases: ['health', 'condition', 'tech health', 'technical health', 'technical condition'],
        example: 'Good',
      },
      {
        key: 'owner_email',
        label: 'Owner email',
        type: 'text',
        required: false,
        description: 'Person accountable for the application.',
        aliases: ['owner', 'application owner', 'accountable'],
        example: 'jo.kim@example.com',
      },
      {
        key: 'annual_run_cost',
        label: 'Annual run cost',
        type: 'money',
        required: false,
        description: 'Recurring annual cost to operate. Leave blank if unknown.',
        aliases: [
          'run cost',
          'opex',
          'annual opex',
          'annual cost',
          'cost',
          'operating cost',
          'annual run cost',
        ],
        example: 480000,
      },
    ],
  },
  {
    id: 'product-applications',
    contractCode: 'DS-006',
    sheet: 'ProductApplications',
    title: 'Application to product mapping',
    summary: 'Which applications support which products',
    purpose:
      'Which applications support which products. One row per pairing — an application supporting three products has three rows.',
    identityField: 'mapping_code',
    cadence: 'Reference',
    ownedBy: 'Enterprise architecture',
    fields: [
      {
        key: 'mapping_code',
        label: 'Mapping code',
        type: 'text',
        required: true,
        description:
          'Your stable identifier for this pairing. If you have none, use product_code + "-" + application_code.',
        aliases: ['id', 'code', 'ref'],
        example: 'EXAMPLE-MAP-1',
      },
      {
        key: 'product_code',
        label: 'Product code',
        type: 'text',
        required: true,
        description: 'From the Products sheet.',
        aliases: ['product', 'product id'],
        example: 'EXAMPLE-PRD-1',
      },
      {
        key: 'application_code',
        label: 'Application code',
        type: 'text',
        required: true,
        description: 'From the Applications sheet.',
        aliases: ['application', 'app', 'app id', 'ci id'],
        example: 'EXAMPLE-APP-1',
      },
      {
        key: 'role',
        label: 'Role',
        type: 'enum',
        required: false,
        description: 'How the application serves the product. Defaults to Supporting.',
        allowed: ['Primary', 'Supporting', 'Shared', 'Dependency'],
        aliases: ['relationship', 'type', 'usage'],
        example: 'Primary',
      },
    ],
  },
  {
    id: 'costs',
    contractCode: 'DS-010',
    sheet: 'Costs',
    title: 'Cost ledger',
    summary: 'Actual spend by cost object and period',
    purpose:
      'Actual spend by cost object and period. Attribution to products is the next sheet — costs land on what incurred them, not on a product.',
    identityField: 'cost_code',
    cadence: 'Monthly',
    ownedBy: 'Finance business partner',
    fields: [
      {
        key: 'cost_code',
        label: 'Cost record code',
        type: 'text',
        required: true,
        description: 'Your stable identifier, typically the ledger line reference.',
        aliases: [
          'id',
          'code',
          'ledger ref',
          'ledger reference',
          'gl reference',
          'gl ref',
          'journal ref',
          'line id',
          'document number',
        ],
        example: 'EXAMPLE-GL-1',
      },
      {
        key: 'cost_object_type',
        label: 'Cost object type',
        type: 'enum',
        required: true,
        description: 'What incurred the cost.',
        allowed: ['Application', 'Team', 'Contract'],
        aliases: ['object type', 'type', 'charged to type', 'cost object type', 'charge type'],
        example: 'Application',
      },
      {
        key: 'cost_object_code',
        label: 'Cost object code',
        type: 'text',
        required: true,
        description: 'The application, team or contract code the cost sits on.',
        aliases: [
          'object',
          'charged to',
          'cost object',
          'cost centre',
          'cost center',
          'app id',
          'ci id',
        ],
        example: 'EXAMPLE-APP-1',
      },
      {
        key: 'period_start',
        label: 'Period start',
        type: 'date',
        required: true,
        description: 'First day of the accounting period. Format YYYY-MM-DD.',
        aliases: ['from', 'start', 'period from'],
        example: '2026-01-01',
      },
      {
        key: 'period_end',
        label: 'Period end',
        type: 'date',
        required: true,
        description: 'Last day of the accounting period. Format YYYY-MM-DD.',
        aliases: ['to', 'end', 'period to'],
        example: '2026-03-31',
      },
      {
        key: 'amount',
        label: 'Amount',
        type: 'money',
        required: true,
        description: 'Spend for the period, in the currency below.',
        aliases: ['cost', 'value', 'spend', 'actual'],
        example: 128400,
      },
      {
        key: 'currency',
        label: 'Currency',
        type: 'text',
        required: true,
        description: 'Three-letter ISO code.',
        aliases: ['ccy', 'curr'],
        example: 'USD',
      },
      {
        key: 'cost_category',
        label: 'Cost category',
        type: 'enum',
        required: false,
        description: 'Run keeps the lights on; Enhance and Transform change the estate.',
        allowed: COST_CATEGORY,
        aliases: ['category', 'run change', 'type'],
        example: 'Run',
      },
    ],
  },
  {
    id: 'cost-allocations',
    contractCode: 'DS-010',
    sheet: 'CostAllocations',
    title: 'Cost attribution',
    summary: 'How each cost splits across products',
    purpose:
      'How each cost record is split across products. Rows need not sum to 100% — whatever is unattributed stays visible rather than being spread.',
    identityField: 'allocation_code',
    cadence: 'Monthly',
    ownedBy: 'Finance business partner',
    fields: [
      {
        key: 'allocation_code',
        label: 'Allocation code',
        type: 'text',
        required: true,
        description: 'Your stable identifier for this split.',
        aliases: ['id', 'code', 'ref'],
        example: 'EXAMPLE-ALC-1',
      },
      {
        key: 'cost_code',
        label: 'Cost record code',
        type: 'text',
        required: true,
        description: 'From the Costs sheet.',
        aliases: ['cost', 'cost id', 'cost ref', 'ledger ref', 'gl reference', 'gl ref'],
        example: 'EXAMPLE-GL-1',
      },
      {
        key: 'product_code',
        label: 'Product code',
        type: 'text',
        required: true,
        description: 'From the Products sheet.',
        aliases: ['product', 'product id'],
        example: 'EXAMPLE-PRD-1',
      },
      {
        key: 'share',
        label: 'Share',
        type: 'number',
        required: true,
        description: 'Decimal between 0 and 1. 0.25 means a quarter of the cost record.',
        aliases: ['percent', 'percentage', 'split', 'allocation', 'share of cost', 'apportionment'],
        example: 0.25,
      },
      {
        key: 'basis',
        label: 'Allocation basis',
        type: 'enum',
        required: false,
        description: 'How the split was arrived at. Recorded so a reader can judge it.',
        allowed: ['Direct', 'Usage', 'Headcount', 'Equal', 'Manual', 'Other'],
        aliases: ['method', 'driver', 'rationale', 'allocation method', 'basis'],
        example: 'Usage',
      },
    ],
  },
  {
    id: 'benefits',
    contractCode: 'DS-010',
    sheet: 'Benefits',
    title: 'Benefit ledger',
    summary: 'Benefit claimed per product, target and realised',
    purpose: 'Benefit claimed by a product, with target and what has actually been realised.',
    identityField: 'benefit_code',
    cadence: 'Monthly',
    ownedBy: 'Value office',
    fields: [
      {
        key: 'benefit_code',
        label: 'Benefit code',
        type: 'text',
        required: true,
        description: 'Your stable identifier for the benefit line.',
        aliases: ['id', 'code', 'ref'],
        example: 'EXAMPLE-BEN-1',
      },
      {
        key: 'product_code',
        label: 'Product code',
        type: 'text',
        required: true,
        description: 'The product claiming the benefit.',
        aliases: ['product', 'product id'],
        example: 'EXAMPLE-PRD-1',
      },
      {
        key: 'benefit_type',
        label: 'Benefit type',
        type: 'enum',
        required: false,
        description: 'Nature of the benefit.',
        allowed: ['OperationalEfficiency', 'CostAvoidance', 'RevenueImpact', 'RiskMitigation'],
        aliases: ['type', 'category'],
        example: 'RevenueImpact',
      },
      {
        key: 'target_amount',
        label: 'Target amount',
        type: 'money',
        required: true,
        description: 'Benefit the business case committed to.',
        aliases: ['target', 'planned', 'committed'],
        example: 1500000,
      },
      {
        key: 'realized_amount',
        label: 'Realised amount',
        type: 'money',
        required: false,
        description: 'Benefit measured so far. Leave blank if not yet measured.',
        aliases: ['realised', 'realized', 'actual', 'achieved', 'delivered', 'to date'],
        example: 940000,
      },
      {
        key: 'currency',
        label: 'Currency',
        type: 'text',
        required: true,
        description: 'Three-letter ISO code.',
        aliases: ['ccy', 'curr'],
        example: 'USD',
      },
      {
        key: 'period_start',
        label: 'Period start',
        type: 'date',
        required: true,
        description: 'Format YYYY-MM-DD.',
        aliases: ['from', 'start'],
        example: '2026-01-01',
      },
      {
        key: 'period_end',
        label: 'Period end',
        type: 'date',
        required: true,
        description: 'Format YYYY-MM-DD.',
        aliases: ['to', 'end'],
        example: '2026-12-31',
      },
    ],
  },
  {
    id: 'teams',
    contractCode: 'DS-011',
    sheet: 'Teams',
    title: 'Teams and capacity',
    summary: 'Delivery teams, capacity and products delivered',
    purpose: 'Delivery teams, their capacity, and the products they deliver.',
    identityField: 'team_code',
    cadence: 'Monthly',
    ownedBy: 'Delivery leadership / HR',
    fields: [
      {
        key: 'team_code',
        label: 'Team code',
        type: 'text',
        required: true,
        description: 'Your stable identifier for the team.',
        aliases: ['id', 'code', 'squad id'],
        example: 'EXAMPLE-TEAM-1',
      },
      {
        key: 'team_name',
        label: 'Team name',
        type: 'text',
        required: true,
        description: 'Name as it should appear on screen.',
        aliases: ['name', 'team', 'squad'],
        example: 'Booking Platform',
      },
      {
        key: 'product_codes',
        label: 'Product codes',
        type: 'text',
        required: false,
        description: 'Products this team delivers. Separate several with a semicolon.',
        aliases: ['products', 'delivers', 'product'],
        example: 'PRD-BOOK;PRD-TRIP',
      },
      {
        key: 'internal_fte',
        label: 'Internal FTE',
        type: 'number',
        required: false,
        description: 'Employed full-time equivalents. Blank if you do not track it.',
        aliases: ['employees', 'internal', 'permanent fte', 'perm fte', 'headcount', 'staff fte'],
        example: 14,
      },
      {
        key: 'contractor_fte',
        label: 'Contractor FTE',
        type: 'number',
        required: false,
        description: 'Contracted full-time equivalents. Blank if you do not track it.',
        aliases: ['contractors', 'contingent', 'contingent fte', 'external fte', 'contractor'],
        example: 6,
      },
      {
        key: 'vacancy_count',
        label: 'Open roles',
        type: 'number',
        required: false,
        description: 'Unfilled positions on the team.',
        aliases: ['vacancies', 'open positions', 'open roles', 'gaps', 'unfilled'],
        example: 2,
      },
    ],
  },
  {
    id: 'suppliers',
    contractCode: 'DS-012',
    sheet: 'Suppliers',
    title: 'Suppliers and spend',
    summary: 'Third parties depended on, and annual spend',
    purpose: 'Third parties the estate depends on, and annual spend with each.',
    identityField: 'supplier_code',
    cadence: 'Reference',
    ownedBy: 'Procurement',
    fields: [
      {
        key: 'supplier_code',
        label: 'Supplier code',
        type: 'text',
        required: true,
        description: 'Your stable identifier, typically the vendor number.',
        aliases: ['id', 'code', 'vendor id', 'vendor no', 'vendor number', 'supplier id'],
        example: 'EXAMPLE-SUP-1',
      },
      {
        key: 'supplier_name',
        label: 'Supplier name',
        type: 'text',
        required: true,
        description: 'Trading name.',
        aliases: ['name', 'vendor', 'supplier'],
        example: 'Meridian Systems',
      },
      {
        key: 'category',
        label: 'Category',
        type: 'text',
        required: false,
        description: 'What the supplier provides.',
        aliases: ['type', 'service', 'segment', 'category', 'spend category'],
        example: 'Managed services',
      },
      {
        key: 'annual_spend',
        label: 'Annual spend',
        type: 'money',
        required: false,
        description: 'Total spend with this supplier for the year.',
        aliases: ['spend', 'annual spend', 'contract value', 'annual value', 'total spend'],
        example: 3200000,
      },
      {
        key: 'currency',
        label: 'Currency',
        type: 'text',
        required: false,
        description: 'Three-letter ISO code.',
        aliases: ['ccy', 'curr'],
        example: 'USD',
      },
      {
        key: 'risk_rating',
        label: 'Risk rating',
        type: 'enum',
        required: false,
        description:
          'Leave blank if the supplier has not been assessed — blank reads as unassessed, not as low risk.',
        allowed: ['Low', 'Medium', 'High'],
        aliases: ['risk', 'rating'],
        example: 'Medium',
      },
    ],
  },
  /*
    The work items themselves, added when the Jira connector landed.

    Until then nothing created a work item. The transition sheet below could
    only describe movement of items somebody else had already loaded, and the
    only thing that had ever loaded one was the seed script — so a client with a
    real tracker could send us their changelog and have every row of it
    refused, because the items it named did not exist. That is a strange
    contract to offer: "send us the history of the items we do not have".

    It is deliberately thin. A tracker holds thirty fields per issue and almost
    all of them are the tracker's business; what this platform needs is the
    identity, what kind of work it is, which product it served, and when it
    opened and closed. Everything the flow screens compute comes from those and
    from the transition sheet. Asking for more would make the mapping step
    longer for no answer anyone gets.
  */
  {
    id: 'work-items',
    contractCode: 'DS-008',
    sheet: 'Work items',
    title: 'Work items',
    summary: 'The items your delivery tool tracks',
    purpose:
      'The tickets, stories and defects your delivery tool holds: what each one is, which product it served, and when it opened and closed. This is what the flow history attaches to, and what lets delivered work be traced to an outcome.',
    identityField: 'work_item_code',
    cadence: 'Weekly',
    ownedBy: 'Delivery tooling / engineering operations',
    fields: [
      {
        key: 'work_item_code',
        label: 'Work item reference',
        type: 'text',
        required: true,
        description:
          'Your tracker’s own key for the item — the one a person would paste into its search box. Re-sending the same key updates the item rather than creating a second one.',
        aliases: ['item', 'issue', 'issue key', 'ticket', 'story', 'work item', 'key', 'code'],
        example: 'EXAMPLE-WI-1',
      },
      {
        key: 'title',
        label: 'Title',
        type: 'text',
        required: true,
        description: 'What the item is called, in your tool’s own words.',
        aliases: ['name', 'summary', 'subject', 'headline'],
        example: 'Recover a disrupted roster within the hour',
      },
      {
        key: 'work_type',
        label: 'Kind of work',
        type: 'enum',
        required: false,
        description:
          'What kind of work this is. Leave blank if your tool does not classify it — blank reads as unclassified, which is reported as such rather than counted as a feature.',
        allowed: ['Feature', 'Defect', 'Debt', 'Compliance', 'Risk'],
        aliases: ['type', 'issue type', 'work type', 'category', 'kind'],
        example: 'Feature',
      },
      {
        key: 'status',
        label: 'Current state',
        type: 'text',
        required: false,
        description:
          'The state the item is in now, in your tool’s own words. Keep your own names — the value stream owner maps them to stages in the interface.',
        aliases: ['state', 'current status', 'workflow status'],
        example: 'Build',
      },
      {
        key: 'product_code',
        label: 'Product reference',
        type: 'text',
        required: false,
        description:
          'The product this work served, as the product portfolio names it. Without it the item is still loaded, but nothing can trace it to an outcome.',
        aliases: ['product', 'product id', 'component', 'service'],
        example: 'EXAMPLE-PROD-1',
      },
      {
        key: 'parent_work_item_code',
        label: 'Parent item reference',
        type: 'text',
        required: false,
        description:
          'The epic or feature this item rolls up into, if your tracker records one.',
        aliases: ['parent', 'epic', 'epic link', 'parent key', 'parent issue'],
        example: 'EXAMPLE-WI-EPIC',
      },
      {
        key: 'opened_at',
        label: 'Opened at',
        type: 'datetime',
        required: false,
        description:
          'When the item was created. Use 2026-03-31T14:05:00Z or 2026-03-31 14:05.',
        aliases: ['created', 'created at', 'opened', 'raised', 'start'],
        example: '2026-03-02T09:00:00Z',
      },
      {
        key: 'closed_at',
        label: 'Closed at',
        type: 'datetime',
        required: false,
        description:
          'When the item was finished. Leave blank while it is still open — a blank here means open, and a date invented for it would report work as delivered that has not been.',
        aliases: ['closed', 'resolved', 'completed', 'done at', 'end'],
        example: '',
      },
      {
        key: 'story_points',
        label: 'Effort points',
        type: 'number',
        required: false,
        description:
          'Your team’s estimate, if they estimate. Leave blank if they do not — blank reads as unknown effort, and zero would read as no effort.',
        aliases: ['points', 'story points', 'estimate', 'effort'],
        example: 5,
      },
    ],
  },
  /*
    The eleventh sheet, and the one that unlocks flow analytics.

    Every other dataset here describes something that *is*: an application, a
    team, a cost. This one describes something that *happened* — a work item
    moved from one state to another at a moment in time — and it is the only
    sheet in the set whose rows are events. That difference shows up in the
    identity field: `transition_code` identifies the movement, not the item, so
    re-sending a corrected changelog updates the same transitions rather than
    doubling an item's history.

    A finished product takes this from a tool changelog through a connector.
    This sheet is the same data by the same identity rule, which is what let
    flow metrics ship before the connector did — and the Jira connector now
    writes exactly these rows, through the same validator and the same commit
    path, so nothing downstream can tell a changelog export from a live sync.
  */
  {
    id: 'work-item-transitions',
    contractCode: 'DS-008',
    sheet: 'Work item flow',
    title: 'Work item state history',
    summary: 'Every state change a work item went through',
    purpose:
      'The state changes your delivery tool recorded: which item moved, out of what, into what, and when. This is what turns lead time into flow time — it is the only way to tell working time from waiting time.',
    identityField: 'transition_code',
    cadence: 'Weekly',
    ownedBy: 'Delivery tooling / engineering operations',
    fields: [
      {
        key: 'transition_code',
        label: 'Change reference',
        type: 'text',
        required: true,
        description:
          'Your stable identifier for this individual state change — most trackers give each changelog entry one. If yours does not, join the item reference and the timestamp.',
        aliases: ['id', 'code', 'change id', 'history id', 'event id', 'changelog id'],
        example: 'EXAMPLE-CHG-1',
      },
      {
        key: 'work_item_code',
        label: 'Work item reference',
        type: 'text',
        required: true,
        description: 'The item that moved, as your tracker names it.',
        aliases: ['item', 'issue', 'issue key', 'ticket', 'story', 'work item', 'key'],
        example: 'EXAMPLE-WI-1',
      },
      {
        key: 'from_state',
        label: 'Moved out of',
        type: 'text',
        required: false,
        description:
          'The state the item left. Leave blank for the first entry in an item\u2019s history — there is no state it came from, and inventing one would put a stage on the map that never existed.',
        aliases: ['from', 'old status', 'previous status', 'from status', 'was'],
        example: 'Build',
      },
      {
        key: 'to_state',
        label: 'Moved into',
        type: 'text',
        required: true,
        description:
          'The state the item entered, in your tool\u2019s own words. Keep your own names — the value stream owner maps them to stages in the interface.',
        aliases: ['to', 'new status', 'status', 'to status', 'became'],
        example: 'Test',
      },
      {
        key: 'flow_state',
        label: 'Kind of state',
        type: 'enum',
        required: false,
        description:
          'Whether work is queued, being worked on, blocked, or finished. Leave blank and the value stream owner decides it when they map the stage; fill it in and it is used until they do.',
        allowed: ['New', 'Active', 'Waiting', 'Done'],
        aliases: ['flow state', 'category', 'state type', 'status category'],
        example: 'Active',
      },
      {
        key: 'changed_at',
        label: 'Changed at',
        type: 'datetime',
        required: true,
        description:
          'When the item entered that state, with the time of day. Use 2026-03-31T14:05:00Z or 2026-03-31 14:05. A date on its own is accepted but reads as midnight, which understates every wait shorter than a day.',
        aliases: ['when', 'timestamp', 'changed', 'date', 'created', 'occurred at', 'transitioned at'],
        example: '2026-03-31T14:05:00Z',
      },
      {
        key: 'parent_work_item_code',
        label: 'Parent item reference',
        type: 'text',
        required: false,
        description:
          'The epic or feature this item rolls up into, if your tracker records one. Sent here because the same export carries it; it is read once per item, not once per change.',
        aliases: ['parent', 'epic', 'epic link', 'parent key', 'parent issue'],
        example: 'EXAMPLE-WI-EPIC',
      },
    ],
  },
] as const;

/**
 * The change column, appended to every sheet.
 *
 * Delta files are the normal case once a client is live: nobody re-sends
 * nineteen hundred applications to tell you one was decommissioned. An import
 * therefore has to carry the *operation*, not just the values — and it has to
 * carry it explicitly, on the row.
 *
 * The alternative, inferring deletion from absence, is the one design that
 * must not be built here. A client who sends a filtered extract, or whose
 * export job half-finishes, would retire everything missing from it. Absence
 * means "not mentioned" and nothing more; removal is something a person states.
 *
 * Appended last so that every file written against the previous templates
 * still validates: the column is optional and blank reads as Upsert.
 */
const CHANGE_FIELD: DatasetField = {
  key: 'change_type',
  label: 'Change',
  type: 'enum',
  required: false,
  description:
    'Leave blank for the usual case: add the row, or update it if the code already exists. Retire removes the record — governed records are retired and kept for history, never erased, so anything already reported against them still reconciles.',
  allowed: ['Upsert', 'Retire'],
  aliases: ['change type', 'operation', 'action', 'cdc', 'delete', 'op', 'change_type'],
  example: '',
};

export const DATASET_TEMPLATES: readonly DatasetTemplate[] = DATASET_DEFINITIONS.map(
  (dataset) => ({ ...dataset, fields: [...dataset.fields, CHANGE_FIELD] }),
);

export const DatasetFieldSchema: z.ZodType<DatasetField> = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  type: z.enum(['text', 'number', 'money', 'date', 'datetime', 'enum', 'boolean']),
  required: z.boolean(),
  description: z.string(),
  allowed: z.array(z.string()).readonly().optional(),
  aliases: z.array(z.string()).readonly().optional(),
  example: z.union([z.string(), z.number()]),
}) as z.ZodType<DatasetField>;

export function datasetById(id: string): DatasetTemplate | undefined {
  const want = id.trim().toLowerCase();
  return DATASET_TEMPLATES.find(
    (d) => d.id.toLowerCase() === want || d.sheet.toLowerCase() === want,
  );
}

/** Every sheet belonging to one `ingest.dataset_contract` code. */
export function datasetsForContract(contractCode: string): DatasetTemplate[] {
  return DATASET_TEMPLATES.filter((d) => d.contractCode === contractCode);
}

/**
 * Best-guess mapping from an arbitrary header to a canonical field.
 *
 * Exact key match wins, then exact label, then a declared alias, then a
 * normalised containment test. Returns null rather than a weak guess: an
 * unmapped column the analyst resolves is recoverable, a wrongly auto-mapped
 * one silently corrupts a column nobody re-reads.
 */
export function suggestField(
  dataset: DatasetTemplate,
  header: string,
): { field: DatasetField; confidence: 'exact' | 'alias' | 'fuzzy' } | null {
  const norm = (s: string) =>
    s
      .trim()
      .toLowerCase()
      .replace(/[\s_\-.]+/g, ' ');
  const h = norm(header);
  if (!h) return null;

  for (const field of dataset.fields) {
    if (norm(field.key) === h || norm(field.label) === h) return { field, confidence: 'exact' };
  }
  for (const field of dataset.fields) {
    if ((field.aliases ?? []).some((a) => norm(a) === h)) return { field, confidence: 'alias' };
  }
  for (const field of dataset.fields) {
    const candidates = [field.key, field.label, ...(field.aliases ?? [])].map(norm);
    if (candidates.some((c) => c.includes(h) || h.includes(c))) {
      return { field, confidence: 'fuzzy' };
    }
  }
  return null;
}
